/**
 * AI Strategy Base Classes
 *
 * Abstract base classes and interfaces for implementing different AI strategies.
 * Provides common structure for v0 (Random), v1 (Greedy), v2 (Minimax), v3 (Advanced).
 */

import {
  BoardPosition,
  PlayerColor,
  AIDifficulty,
  PERFORMANCE_TARGETS,
} from '@/types/game-types';
import type { GameState } from '@/core/models/GameState';
// import type { Move } from '@/core/models/Move';

/**
 * AI move selection result
 */
export interface AIMoveResult {
  readonly position: BoardPosition;
  readonly confidence: number; // 0-1, how confident the AI is in this move
  readonly evaluation: number; // Numeric evaluation score
  readonly thinkingTime: number; // Time spent calculating (ms)
  readonly depth?: number; // Search depth used (for minimax variants)
  readonly nodesEvaluated?: number; // Nodes explored (for analytics)
}

/**
 * AI thinking process metadata
 */
export interface AIThinkingProcess {
  readonly startTime: number;
  readonly targetTime: number; // Maximum thinking time allowed
  readonly depth: number; // Current search depth
  readonly nodesEvaluated: number;
  readonly bestMove?: BoardPosition;
  readonly bestScore?: number;
}

/**
 * AI performance metrics
 */
export interface AIPerformanceMetrics {
  readonly averageThinkingTime: number;
  readonly movesPlayed: number;
  readonly totalNodesEvaluated: number;
  readonly averageDepth: number;
  readonly timeoutCount: number; // How many times AI hit time limit
  readonly accuracy: number; // Estimated move quality (0-1)
}

/**
 * Base interface for all AI strategies
 */
export interface IAIStrategy {
  readonly difficulty: AIDifficulty;
  readonly name: string;
  readonly description: string;

  /**
   * Select the best move for the given game state
   */
  selectMove(
    gameState: GameState,
    player: PlayerColor,
    timeLimit?: number
  ): Promise<AIMoveResult>;

  /**
   * Get current performance metrics
   */
  getPerformanceMetrics(): AIPerformanceMetrics;

  /**
   * Reset performance metrics
   */
  resetMetrics(): void;

  /**
   * Check if AI is currently thinking
   */
  isThinking(): boolean;

  /**
   * Stop current thinking process (for timeout/cancellation)
   */
  stopThinking(): void;
}

/**
 * Abstract base class for AI strategies
 */
export abstract class AIStrategyBase implements IAIStrategy {
  public abstract readonly difficulty: AIDifficulty;
  public abstract readonly name: string;
  public abstract readonly description: string;

  protected _isThinking = false;
  protected _shouldStop = false;
  protected _metrics: AIPerformanceMetrics = {
    averageThinkingTime: 0,
    movesPlayed: 0,
    totalNodesEvaluated: 0,
    averageDepth: 0,
    timeoutCount: 0,
    accuracy: 0,
  };

  protected _thinkingHistory: number[] = [];
  protected _depthHistory: number[] = [];
  protected _nodeHistory: number[] = [];

  /**
   * Main move selection interface
   */
  async selectMove(
    gameState: GameState,
    player: PlayerColor,
    timeLimit?: number,
  ): Promise<AIMoveResult> {
    const startTime = Date.now();
    const targetTime = timeLimit || this.getDefaultTimeLimit();

    this._isThinking = true;
    this._shouldStop = false;

    try {
      const result = await this.selectMoveImpl(gameState, player, targetTime);
      const actualThinkingTime = Date.now() - startTime;

      // Update metrics
      this.updateMetrics(result, actualThinkingTime, targetTime);

      return {
        ...result,
        thinkingTime: actualThinkingTime,
      };
    } finally {
      this._isThinking = false;
      this._shouldStop = false;
    }
  }

  /**
   * Abstract method that subclasses must implement
   */
  protected abstract selectMoveImpl(
    gameState: GameState,
    player: PlayerColor,
    timeLimit: number
  ): Promise<AIMoveResult>;

  /**
   * Get default time limit for this difficulty level
   */
  protected getDefaultTimeLimit(): number {
    return PERFORMANCE_TARGETS.AI_RESPONSE_TIME[this.difficulty] || 1000;
  }

  /**
   * Check if we should stop thinking (timeout or cancellation)
   */
  protected shouldStop(startTime: number, timeLimit: number): boolean {
    return this._shouldStop || (Date.now() - startTime) >= timeLimit;
  }

  /**
   * Update performance metrics after a move
   */
  protected updateMetrics(
    result: AIMoveResult,
    actualTime: number,
    timeLimit: number,
  ): void {
    this._thinkingHistory.push(actualTime);
    if (result.depth !== undefined) {
      this._depthHistory.push(result.depth);
    }
    if (result.nodesEvaluated !== undefined) {
      this._nodeHistory.push(result.nodesEvaluated);
    }

    const movesPlayed = this._thinkingHistory.length;
    const totalThinkingTime = this._thinkingHistory.reduce((sum, time) => sum + time, 0);
    const totalNodes = this._nodeHistory.reduce((sum, nodes) => sum + nodes, 0);
    const totalDepth = this._depthHistory.reduce((sum, depth) => sum + depth, 0);

    this._metrics = {
      averageThinkingTime: totalThinkingTime / movesPlayed,
      movesPlayed,
      totalNodesEvaluated: totalNodes,
      averageDepth: this._depthHistory.length > 0 ? totalDepth / this._depthHistory.length : 0,
      timeoutCount: this._metrics.timeoutCount + (actualTime >= timeLimit ? 1 : 0),
      accuracy: this.estimateAccuracy(),
    };
  }

  /**
   * Estimate move accuracy based on thinking time and difficulty
   */
  protected estimateAccuracy(): number {
    if (this._metrics.movesPlayed === 0) {return 0;}

    const targetTime = this.getDefaultTimeLimit();
    const actualTime = this._metrics.averageThinkingTime;

    // Simple heuristic: accuracy increases with thinking time up to target
    const timeRatio = Math.min(actualTime / targetTime, 1.0);
    const baseAccuracy = this.getBaseAccuracy();

    return Math.min(baseAccuracy + (timeRatio * 0.3), 1.0);
  }

  /**
   * Get base accuracy for this difficulty level
   */
  protected getBaseAccuracy(): number {
    switch (this.difficulty) {
      case AIDifficulty.RANDOM: return 0.1;  // Very low accuracy
      case AIDifficulty.GREEDY: return 0.6;  // Decent accuracy
      case AIDifficulty.MINIMAX: return 0.8; // High accuracy
      case AIDifficulty.ADVANCED: return 0.9; // Very high accuracy
      default: return 0.5;
    }
  }

  /**
   * Get current performance metrics
   */
  getPerformanceMetrics(): AIPerformanceMetrics {
    return { ...this._metrics };
  }

  /**
   * Reset performance metrics
   */
  resetMetrics(): void {
    this._metrics = {
      averageThinkingTime: 0,
      movesPlayed: 0,
      totalNodesEvaluated: 0,
      averageDepth: 0,
      timeoutCount: 0,
      accuracy: 0,
    };
    this._thinkingHistory = [];
    this._depthHistory = [];
    this._nodeHistory = [];
  }

  /**
   * Check if AI is currently thinking
   */
  isThinking(): boolean {
    return this._isThinking;
  }

  /**
   * Stop current thinking process
   */
  stopThinking(): void {
    this._shouldStop = true;
  }
}

/**
 * Utility functions for AI implementations
 */
export class AIUtils {
  /**
   * Add random jitter to move selection to avoid predictable play
   */
  static addJitter(baseScore: number, jitterAmount = 0.1): number {
    const jitter = (Math.random() - 0.5) * 2 * jitterAmount;
    return baseScore + jitter;
  }

  /**
   * Select move from candidates with weighted randomization
   */
  static selectWeightedRandom<T extends { score: number }>(
    candidates: T[],
    temperature = 1.0,
  ): T {
    if (candidates.length === 0) {
      throw new Error('No candidates to select from');
    }

    if (candidates.length === 1) {
      return candidates[0] as T;
    }

    // Apply temperature scaling to scores
    const scaledCandidates = candidates.map(candidate => ({
      ...candidate,
      weight: Math.exp(candidate.score / temperature),
    }));

    const totalWeight = scaledCandidates.reduce((sum, candidate) => sum + candidate.weight, 0);
    const randomValue = Math.random() * totalWeight;

    let cumulativeWeight = 0;
    for (const candidate of scaledCandidates) {
      cumulativeWeight += candidate.weight;
      if (randomValue <= cumulativeWeight) {
        return candidate as T;
      }
    }

    // Fallback to last candidate (this shouldn't happen but ensures type safety)
    return scaledCandidates[scaledCandidates.length - 1] as T;
  }

  /**
   * Evaluate position stability for AI decision making
   */
  static evaluatePositionStability(
    gameState: GameState,
    position: BoardPosition,
    player: PlayerColor,
  ): number {
    // Simple stability heuristic based on position type and surrounding pieces
    let stability = 0;

    // Corner positions are extremely stable
    if ([0, 7, 56, 63].includes(position)) {
      stability += 10;
    }

    // Edge positions have moderate stability
    const row = Math.floor(position / 8);
    const col = position % 8;
    if (row === 0 || row === 7 || col === 0 || col === 7) {
      stability += 3;
    }

    // Check for friendly neighbors (increases stability)
    const neighbors = this.getNeighbors(position);
    const friendlyNeighbors = neighbors.filter(pos =>
      gameState.board[pos] === player,
    ).length;

    stability += friendlyNeighbors * 0.5;

    return stability;
  }

  /**
   * Get neighboring positions
   */
  private static getNeighbors(position: BoardPosition): BoardPosition[] {
    const neighbors: BoardPosition[] = [];
    const row = Math.floor(position / 8);
    const col = position % 8;

    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        if (dr === 0 && dc === 0) {continue;}

        const newRow = row + dr;
        const newCol = col + dc;

        if (newRow >= 0 && newRow < 8 && newCol >= 0 && newCol < 8) {
          neighbors.push(newRow * 8 + newCol);
        }
      }
    }

    return neighbors;
  }

  /**
   * Calculate endgame phase (0 = early game, 1 = endgame)
   */
  static getGamePhase(gameState: GameState): number {
    const totalPieces = gameState.scores.black + gameState.scores.white;
    const maxPieces = 64;
    return totalPieces / maxPieces;
  }

  /**
   * Simple mobility calculation
   */
  static calculateMobility(gameState: GameState, _player: PlayerColor): number {
    return gameState.validMoves.size;
  }

  /**
   * Check if position is in dangerous area (X-squares, C-squares)
   */
  static isDangerousPosition(position: BoardPosition): boolean {
    // X-squares (diagonal from corners) and C-squares (adjacent to corners)
    const dangerousPositions = [
      1, 6, 8, 9, 14, 15, // Top area
      48, 49, 54, 55, 57, 62, // Bottom area
    ];
    return dangerousPositions.includes(position);
  }
}